import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Clock, Zap, Receipt, ArrowRight, TrendingUp, Plus, Loader2, Minus, ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useCustomerAuth } from "@/context/CustomerAuthContext";
import { KES, formatDuration, formatBandwidth, formatCountdown } from "@/lib/format";

// Mirrors the backend's computeAddDevicesOrder discount math exactly, for an
// accurate pre-payment preview (the server remains the source of truth for
// what's actually charged).
function computeAddDevicesTotal(pricePerDevice, count) {
  const n = Math.min(20, Math.max(1, count));
  let total = pricePerDevice * n;
  if (n >= 10) total *= 0.75;
  return Math.round(total * 100) / 100;
}

const PAGE_SIZE = 10;

function HistoryPager({ page, total, onChange, label }) {
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  return (
    <div className="flex items-center justify-between px-5 py-3 border-t border-[#E1E4E8] text-xs font-mono text-[#57606A]">
      <div>{total === 0 ? `0 ${label}` : `${(page - 1) * PAGE_SIZE + 1}–${Math.min(page * PAGE_SIZE, total)} of ${total}`}</div>
      <div className="flex items-center gap-2">
        <button
          type="button" disabled={page <= 1} onClick={() => onChange(Math.max(1, page - 1))}
          className="h-7 w-7 rounded-full border border-[#E1E4E8] grid place-items-center disabled:opacity-30 hover:bg-[#F7F7F5]"
        ><ChevronLeft className="h-3.5 w-3.5" /></button>
        <span>Page {page} / {totalPages}</span>
        <button
          type="button" disabled={page >= totalPages} onClick={() => onChange(Math.min(totalPages, page + 1))}
          className="h-7 w-7 rounded-full border border-[#E1E4E8] grid place-items-center disabled:opacity-30 hover:bg-[#F7F7F5]"
        ><ChevronRight className="h-3.5 w-3.5" /></button>
      </div>
    </div>
  );
}

export default function CustomerDashboard() {
  const { customer } = useCustomerAuth();
  const [me, setMe] = useState(null);
  const [subs, setSubs] = useState([]);
  const [subsTotal, setSubsTotal] = useState(0);
  const [subsPage, setSubsPage] = useState(1);
  const [tx, setTx] = useState([]);
  const [txTotal, setTxTotal] = useState(0);
  const [txPage, setTxPage] = useState(1);
  const [packages, setPackages] = useState([]);
  const [now, setNow] = useState(Date.now());
  const [actionBusy, setActionBusy] = useState(null); // "extend" | "upgrade" | "add_devices" | null
  const [pendingIntent, setPendingIntent] = useState(null); // { transactionId, intent }
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [addDevicesOpen, setAddDevicesOpen] = useState(false);
  const [extraDevices, setExtraDevices] = useState(1);

  const load = async () => {
    const [m, s, t, p] = await Promise.all([
      api.get("/customer/me"),
      api.get("/customer/subscriptions", { params: { page: subsPage, page_size: PAGE_SIZE } }),
      api.get("/customer/transactions", { params: { page: txPage, page_size: PAGE_SIZE } }),
      api.get("/portal/packages"),
    ]);
    setMe(m.data);
    setSubs(s.data.items); setSubsTotal(s.data.total);
    setTx(t.data.items); setTxTotal(t.data.total);
    setPackages(p.data);
  };
  useEffect(() => { load(); }, [subsPage, txPage]);

  useEffect(() => {
    const iv = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(iv);
  }, []);

  // Shared M-Pesa-wait poller for extend/upgrade/add-devices, mirroring the
  // captive portal's own transaction-polling pattern.
  useEffect(() => {
    if (!pendingIntent) return;
    let alive = true;
    const iv = setInterval(async () => {
      try {
        const { data } = await api.get(`/portal/transaction/${pendingIntent.transactionId}`);
        if (!alive) return;
        if (data.status === "success") {
          toast.success("Done — your plan is updated.");
          setPendingIntent(null);
          setActionBusy(null);
          await load();
        } else if (data.status === "failed") {
          toast.error("Payment failed. Please try again.");
          setPendingIntent(null);
          setActionBusy(null);
        }
      } catch { /* transient — keep polling */ }
    }, 1500);
    return () => { alive = false; clearInterval(iv); };
  }, [pendingIntent]);

  const startAction = async (intent, endpoint, body) => {
    setActionBusy(intent);
    try {
      const { data } = await api.post(endpoint, body);
      toast.success(data.message);
      setPendingIntent({ transactionId: data.transaction_id, intent });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      setActionBusy(null);
    }
  };

  const extend = () => startAction("extend", "/customer/subscription/extend", {});
  const upgrade = (pkg) => {
    setUpgradeOpen(false);
    startAction("upgrade", "/customer/subscription/upgrade", { package_id: pkg.id });
  };
  const addDevices = () => {
    setAddDevicesOpen(false);
    startAction("add_devices", "/customer/subscription/add-devices", { additional_devices: extraDevices });
  };

  if (!me) return null;
  const sub = me.active_subscription;
  const activeDevices = me.active_devices || [];
  const hasAnyActive = !!sub || activeDevices.length > 0;
  const currentPkg = sub ? packages.find((p) => p.id === sub.package_id) : null;
  const otherPackages = packages.filter((p) => p.id !== sub?.package_id);
  const busy = actionBusy !== null;

  return (
    <div data-testid="customer-dashboard">
      <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#43B02A]">// HI {customer.phone}</div>
      <h1 className="mt-1 font-heading font-black text-4xl">Your account</h1>

      {hasAnyActive ? (
        <div className="mt-8 border border-[#E1E4E8] bg-white p-8" data-testid="customer-active-sub">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 grid place-items-center bg-[#43B02A]/10">
              <Zap className="h-5 w-5 text-[#43B02A]" />
            </div>
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#43B02A]">// ONLINE</div>
              <div className="font-heading font-black text-2xl">
                {activeDevices.length > 1
                  ? `${activeDevices.length} devices online`
                  : sub?.package_name || activeDevices[0]?.package_name}
              </div>
            </div>
          </div>

          {activeDevices.length > 0 ? (
            <div className="mt-6 divide-y divide-[#E1E4E8] border-y border-[#E1E4E8]">
              {activeDevices.map((d) => (
                <div key={d.device_id} data-testid={`active-device-${d.device_id}`} className="py-4 flex items-center justify-between gap-4">
                  <div className="min-w-0">
                    <div className="font-medium truncate">{d.device_name || d.mac_address}</div>
                    <div className="font-mono text-xs text-[#57606A] mt-0.5">{d.package_name} · ↓{formatBandwidth(d.download_kbps)}</div>
                  </div>
                  <div className="font-mono font-black text-xl tabular-nums text-[#43B02A] shrink-0" data-testid={`active-device-countdown-${d.device_id}`}>
                    {formatCountdown(Math.max(0, new Date(d.expires_at).getTime() - now))}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="mt-6 py-6 border-y border-[#E1E4E8] text-center">
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#57606A]">TIME REMAINING</div>
              <div className="font-mono font-black text-6xl mt-2 tabular-nums" data-testid="customer-countdown">
                {formatCountdown(Math.max(0, new Date(sub.expires_at).getTime() - now))}
              </div>
            </div>
          )}

          {sub && (
            <>
              <div className="mt-4 grid grid-cols-2 gap-4 text-sm font-mono">
                <div><span className="text-[#57606A]">↓ Download</span> <span className="font-bold">{formatBandwidth(sub.download_kbps)}</span></div>
                <div><span className="text-[#57606A]">↑ Upload</span> <span className="font-bold">{formatBandwidth(sub.upload_kbps)}</span></div>
              </div>
              <div className="mt-6 pt-6 border-t border-[#E1E4E8] flex flex-wrap gap-3">
                <Button data-testid="extend-sub-btn" variant="outline" onClick={extend} disabled={busy}
                        className="h-11 rounded-full border-[#1A1C20] text-[#1A1C20] hover:bg-[#1A1C20] hover:text-white font-bold">
                  {actionBusy === "extend" ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Clock className="h-4 w-4 mr-2" /> EXTEND</>}
                </Button>
                {otherPackages.length > 0 && (
                  <Button data-testid="upgrade-sub-btn" variant="outline" onClick={() => setUpgradeOpen(true)} disabled={busy}
                          className="h-11 rounded-full border-[#1A1C20] text-[#1A1C20] hover:bg-[#1A1C20] hover:text-white font-bold">
                    {actionBusy === "upgrade" ? <Loader2 className="h-4 w-4 animate-spin" /> : <><TrendingUp className="h-4 w-4 mr-2" /> UPGRADE</>}
                  </Button>
                )}
                {currentPkg?.per_device_pricing && (
                  <Button data-testid="add-devices-btn" variant="outline" onClick={() => { setExtraDevices(1); setAddDevicesOpen(true); }} disabled={busy}
                          className="h-11 rounded-full border-[#1A1C20] text-[#1A1C20] hover:bg-[#1A1C20] hover:text-white font-bold">
                    {actionBusy === "add_devices" ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Plus className="h-4 w-4 mr-2" /> ADD DEVICES</>}
                  </Button>
                )}
              </div>
              {pendingIntent && (
                <p className="mt-3 text-xs font-mono text-[#57606A]">Waiting for M-Pesa confirmation…</p>
              )}
            </>
          )}
          {!sub && (
            <p className="mt-6 text-xs font-mono text-[#57606A]">
              Your original plan has ended, but the device(s) above already claimed their own online time and will keep working until it runs out.
            </p>
          )}
        </div>
      ) : (
        <div className="mt-8 border-2 border-dashed border-[#E1E4E8] bg-white p-8 text-center" data-testid="customer-no-sub">
          <Clock className="h-8 w-8 text-[#57606A] mx-auto" />
          <h2 className="font-heading font-black text-2xl mt-4">No active subscription</h2>
          <p className="text-[#57606A] mt-2">Buy a package on the captive portal to get online.</p>
          <Link to="/">
            <Button className="mt-5 h-11 rounded-full bg-[#43B02A] hover:bg-[#368F22] text-white font-bold">
              Browse packages <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
          </Link>
        </div>
      )}

      <section className="mt-10">
        <div className="flex items-center justify-between">
          <h2 className="font-heading font-black text-xl flex items-center gap-2"><Receipt className="h-5 w-5" /> Recent payments</h2>
          <div className="text-xs text-[#57606A] font-mono">{txTotal} txn</div>
        </div>
        <div className="mt-3 border border-[#E1E4E8] bg-white divide-y divide-[#E1E4E8]">
          {tx.length === 0 && <div className="p-8 text-center text-sm text-[#57606A]">No transactions yet.</div>}
          {tx.map((t) => (
            <div key={t.id} data-testid={`customer-tx-${t.id}`} className="px-5 py-3 flex flex-col gap-1.5 sm:grid sm:grid-cols-4 sm:items-center sm:gap-4 text-sm">
              <div className="flex items-center justify-between gap-3 sm:block">
                <div>
                  <div className="font-medium">{t.package_name || "Package"}</div>
                  <div className="font-mono text-xs text-[#57606A]">{new Date(t.created_at).toLocaleString()}</div>
                </div>
                <div className={`font-mono text-xs uppercase tracking-[0.15em] sm:hidden ${
                  t.status === "success" ? "text-[#43B02A]" : t.status === "failed" ? "text-[#FF3B30]" : "text-[#57606A]"
                }`}>{t.status}</div>
              </div>
              <div className="font-mono text-xs text-[#57606A]">{t.mpesa_receipt || "-"}</div>
              <div className={`hidden sm:block font-mono text-xs uppercase tracking-[0.15em] ${
                t.status === "success" ? "text-[#43B02A]" : t.status === "failed" ? "text-[#FF3B30]" : "text-[#57606A]"
              }`}>{t.status}</div>
              <div className="font-mono font-bold tabular-nums sm:text-right">{KES(t.amount_kes)}</div>
            </div>
          ))}
          {tx.length > 0 && <HistoryPager page={txPage} total={txTotal} onChange={setTxPage} label="transactions" />}
        </div>
      </section>

      <section className="mt-10">
        <h2 className="font-heading font-black text-xl">Subscription history</h2>
        <div className="mt-3 border border-[#E1E4E8] bg-white divide-y divide-[#E1E4E8]">
          {subs.length === 0 && <div className="p-8 text-center text-sm text-[#57606A]">No subscriptions yet.</div>}
          {subs.map((s) => (
            <div key={s.id} data-testid={`customer-sub-${s.id}`} className="px-5 py-3 flex flex-col gap-1.5 sm:grid sm:grid-cols-4 sm:items-center sm:gap-4 text-sm">
              <div className="flex items-center justify-between gap-3 sm:contents">
                <div className="font-medium sm:order-1">{s.package_name}</div>
                <div className={`font-mono text-xs uppercase tracking-[0.15em] sm:order-4 sm:text-right ${
                  s.status === "active" ? "text-[#43B02A]" : "text-[#57606A]"
                }`}>{s.status}</div>
              </div>
              <div className="font-mono text-xs text-[#57606A] sm:order-2">{formatDuration(s.duration_minutes)}</div>
              <div className="font-mono text-xs text-[#57606A] sm:order-3">{s.expires_at ? new Date(s.expires_at).toLocaleString() : "-"}</div>
            </div>
          ))}
          {subs.length > 0 && <HistoryPager page={subsPage} total={subsTotal} onChange={setSubsPage} label="subscriptions" />}
        </div>
      </section>

      <Dialog open={upgradeOpen} onOpenChange={setUpgradeOpen}>
        <DialogContent className="bg-white rounded-none max-w-sm" data-testid="upgrade-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Switch plans</DialogTitle>
            <DialogDescription>Time remaining on your current plan carries over — you just don't lose what you already paid for.</DialogDescription>
          </DialogHeader>
          <div className="max-h-80 overflow-y-auto divide-y divide-[#E1E4E8] border border-[#E1E4E8]">
            {otherPackages.map((p) => (
              <button key={p.id} data-testid={`upgrade-option-${p.id}`} onClick={() => upgrade(p)}
                      className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-[#F7F7F5]">
                <div>
                  <div className="font-medium">{p.name}</div>
                  <div className="text-xs text-[#57606A] font-mono">{formatDuration(p.duration_minutes)} · ↓{formatBandwidth(p.download_kbps)}</div>
                </div>
                <div className="font-mono font-bold text-[#43B02A]">{KES(p.price_kes)}</div>
              </button>
            ))}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setUpgradeOpen(false)} className="rounded-none">Cancel</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={addDevicesOpen} onOpenChange={setAddDevicesOpen}>
        <DialogContent className="bg-white rounded-none max-w-sm" data-testid="add-devices-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Add device slots</DialogTitle>
            <DialogDescription>Currently {sub?.simultaneous_devices || 0} devices allowed at once.</DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <Label className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#57606A]">How many more devices?</Label>
            <div className="mt-2 flex items-center gap-4">
              <button onClick={() => setExtraDevices((n) => Math.max(1, n - 1))} className="h-11 w-11 grid place-items-center border border-[#E1E4E8] hover:bg-[#F7F7F5]">
                <Minus className="h-4 w-4" />
              </button>
              <div className="flex-1 text-center font-mono font-black text-3xl tabular-nums">{extraDevices}</div>
              <button onClick={() => setExtraDevices((n) => Math.min(20, n + 1))} className="h-11 w-11 grid place-items-center border border-[#E1E4E8] hover:bg-[#F7F7F5]">
                <Plus className="h-4 w-4" />
              </button>
            </div>
            {currentPkg?.per_device_pricing && (
              <p className="mt-3 text-center font-mono text-sm text-[#57606A]">
                Total: <span className="font-bold text-[#1A1C20]">{KES(computeAddDevicesTotal(currentPkg.price_kes, extraDevices))}</span>
                {extraDevices >= 10 && <span className="block text-xs text-[#43B02A] mt-0.5">25% off for 10+ devices</span>}
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setAddDevicesOpen(false)} className="rounded-none">Cancel</Button>
            <Button onClick={addDevices} className="rounded-none bg-[#43B02A] hover:bg-[#368F22] text-white font-bold">PAY & ADD</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
